import { AuthUser } from '../core/state/auth-store';
import { startBaseView } from './base-view';
import { openCurrencyShopUI } from './base-view/ui/currency-shop-ui';
import { resumeReturnedDeposit } from './state/currency-shop-state';
import { stopLandingAudio } from './landing/landing-audio';
import { startLocationView } from './base-view/location-view';
import { startArenaView } from './base-view/arena-view';
import { mountLandscapeGate } from './landscape-gate';
import { locationStateStore } from './state/location-state';
import { arenaStateStore, connectArena } from './state/arena-state';
import { connectBattlegroundOutcome, resetBattleground } from './state/battleground-state';
import { MAP_REGISTRY } from './base-view/data/maps/registry';
import { loadCharacterModel } from './character-select/load-model';
import { CHARACTERS } from './character-select/characters';
import { startCharacterSelect } from './character-select';
import { startTutorialView } from './base-view/tutorial/tutorial-view';
import { tutorialYardStore, clearTutorialYardRequest } from './state/tutorial-yard-state';
import {
  loadGameState, createCharacter, connectGameStateSync, disconnectGameStateSync, resetGameState,
  gameStateStore,
} from './state/game-state';
import { resetNpcState } from './state/npc-state';
import { resetQuestState } from './state/quest-state';
import {
  createSettingsUI, disposeSettingsUI, openSettingsUI,
} from './settings/settings-ui';
import { escapeHtml } from './util';

let currentTeardown: (() => void) | null = null;

/** Id of the one-per-page stylesheet for the top-right chrome — see `ensureChromeStyles`. */
const CHROME_STYLE_ID = 'tg-chrome-styles';

/**
 * 24x24, stroke-only, `currentColor` — same convention as the action bar's
 * icons.
 *
 * A real toothed cog outline, not a hub with eight radial ticks: the tick
 * version is the shape every "brightness" glyph uses, and rendered at 15 px it
 * read as a small sun. The teeth are what make it a gear. Generated as an
 * 8-tooth polygon (outer 9.4, root 6.9, 23-degree teeth) rather than drawn by
 * hand, so the spacing is exact.
 */
const GEAR_ICON = '<path d="M10 5.4 L10.1 2.8 L13.9 2.8 L14 5.4 L15.2 5.9 L17.2 4.2 L19.8 6.8'
  + ' L18.1 8.8 L18.6 10 L21.2 10.1 L21.2 13.9 L18.6 14 L18.1 15.2 L19.8 17.2 L17.2 19.8'
  + ' L15.2 18.1 L14 18.6 L13.9 21.2 L10.1 21.2 L10 18.6 L8.8 18.1 L6.8 19.8 L4.2 17.2'
  + ' L5.9 15.2 L5.4 14 L2.8 13.9 L2.8 10.1 L5.4 10 L5.9 8.8 L4.2 6.8 L6.8 4.2 L8.8 5.9 Z"/>'
  + '<circle cx="12" cy="12" r="3.1"/>';

/** A door with an arrow leaving it. */
const EXIT_ICON = '<path d="M15.2 8.4V5.9a1.7 1.7 0 0 0-1.7-1.7H6.2A1.7 1.7 0 0 0 4.5 5.9v12.2'
  + 'a1.7 1.7 0 0 0 1.7 1.7h7.3a1.7 1.7 0 0 0 1.7-1.7v-2.5"/>'
  + '<path d="M10.4 12h9.3"/><path d="m16.8 8.9 3.1 3.1-3.1 3.1"/>';

function chromeIcon(paths: string): string {
  return '<svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor"'
    + ` stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">${paths}</svg>`;
}

/**
 * Grungy: scorched metal rather than a rounded web button. Three things do the
 * work — a dark vertical gradient under sparse diagonal grime streaks, a
 * clipped corner on each outer edge so the silhouette is cut rather than
 * rounded, and a warm rust red that reads as oxidised steel, not as a browser
 * error state. Sign out is the destructive one and gets the red; the gear
 * stays neutral brass so the pair does not read as two warnings.
 *
 * The streak layer is DARK-ONLY and sparse (2px of grime every 11px). The
 * first pass alternated a dark and a light band every 5px, which at button
 * size stopped reading as wear and started reading as hazard tape — visible
 * only once it was rendered, not in the CSS.
 */
const CHROME_CSS = `
.tg-chrome {
  position: absolute; top: 12px; right: 12px; z-index: 20;
  display: flex; align-items: stretch; gap: 8px;
}
.tg-chrome-btn {
  display: inline-flex; align-items: center; gap: 7px;
  padding: 7px 11px; cursor: pointer;
  font: 700 11px/1 ui-monospace, "SFMono-Regular", Menlo, monospace;
  letter-spacing: 0.14em; text-transform: uppercase;
  border: 1px solid #6b4a24; color: #e8d6b6;
  background:
    repeating-linear-gradient(115deg, rgba(0,0,0,0.20) 0 2px, rgba(0,0,0,0) 2px 11px),
    linear-gradient(180deg, #2b1d10, #140c06);
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.07), inset 0 -2px 6px rgba(0,0,0,0.6), 0 6px 18px rgba(0,0,0,0.55);
  text-shadow: 0 1px 2px rgba(0,0,0,0.85);
  clip-path: polygon(0 0, calc(100% - 9px) 0, 100% 9px, 100% 100%, 9px 100%, 0 calc(100% - 9px));
  transition: color 0.12s ease, border-color 0.12s ease, filter 0.12s ease;
}
.tg-chrome-btn svg { width: 15px; height: 15px; display: block; flex: none; }
.tg-chrome-btn:hover { filter: brightness(1.25); }
.tg-chrome-btn:focus-visible { outline: 2px solid #b08a4e; outline-offset: 2px; }
.tg-chrome-gear { padding: 7px 8px; color: #c9a86e; }
.tg-chrome-gear:hover { color: #f0d9a4; }
.tg-chrome-signout {
  color: #f0a893; border-color: #7e2b1f;
  background:
    repeating-linear-gradient(115deg, rgba(0,0,0,0.22) 0 2px, rgba(0,0,0,0) 2px 11px),
    linear-gradient(180deg, #4a1712, #250b08);
}
.tg-chrome-signout:hover { color: #ffd3c4; border-color: #a8452c; }

/* ── Sign-out confirmation ──────────────────────────────────────────────
   Its OWN fixed overlay, not a child of .tg-chrome.

   The chrome bar is z-index 20 and creates a stacking context, while every
   game panel is a full-screen scrim at 1001-1100 and the player frame sits at
   1002. A confirm rendered inside the chrome would therefore paint UNDER any
   open window — the click would appear to do nothing and the UI would read as
   frozen. 1300 clears the tooltip layer (1200) as well.

   Palette is literal hex to match the rest of this file. The comment above
   about tokens being unavailable here is WRONG and is corrected there; the
   reason to keep hex is simply that this toolbar is one self-contained visual
   unit and half-tokenising it would be worse than either choice. */
.tg-confirm {
  position: fixed; inset: 0; z-index: 1300;
  display: flex; align-items: center; justify-content: center;
  background: rgba(8,5,3,0.72);
  backdrop-filter: blur(3px);
}
.tg-confirm[hidden] { display: none; }
.tg-confirm-card {
  width: min(380px, 90vw); padding: 22px 24px; text-align: center;
  color: #e8d6b6;
  background: linear-gradient(170deg, #2a2116, #16100a);
  border: 1px solid #6b4a24;
  box-shadow: 0 16px 48px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.06);
}
.tg-confirm-title {
  margin: 0 0 6px;
  font: 700 18px/1.2 ui-monospace, "SFMono-Regular", Menlo, monospace;
  letter-spacing: 0.12em; text-transform: uppercase; color: #f0a893;
}
.tg-confirm-body { margin: 0 0 18px; font: 13px/1.5 Georgia, serif; color: #bfae8e; }
.tg-confirm-actions { display: flex; gap: 10px; justify-content: center; }
.tg-confirm-btn {
  min-width: 118px; padding: 9px 16px; cursor: pointer;
  font: 700 12px/1.2 ui-monospace, "SFMono-Regular", Menlo, monospace;
  letter-spacing: 0.1em; text-transform: uppercase;
  border: 1px solid #6b4a24; color: #e8d6b6; background: rgba(107,74,36,0.25);
}
.tg-confirm-btn:hover { background: rgba(107,74,36,0.5); }
.tg-confirm-btn:focus-visible { outline: 2px solid #b08a4e; outline-offset: 2px; }
.tg-confirm-go {
  color: #ffd3c4; border-color: #a8452c;
  background: linear-gradient(180deg, #4a1712, #250b08);
}
.tg-confirm-go:hover { background: linear-gradient(180deg, #5d1d16, #310e09); }
`;

/**
 * Injects the chrome stylesheet once per page.
 *
 * Module-level, not nested in `mountGame`: `confirmDialog` is also module-level
 * (its overlay must escape the chrome's stacking context) and needs the same
 * sheet. Deliberately never removed — `makeSignOut` runs again on every
 * settlement mount, and a sheet with no elements to match costs nothing.
 */
function ensureChromeStyles(): void {
  if (document.getElementById(CHROME_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = CHROME_STYLE_ID;
  style.textContent = CHROME_CSS;
  document.head.appendChild(style);
}

/**
 * Ask before doing something with no undo.
 *
 * Returns a teardown, and calls `onConfirm` only if the player says yes.
 * Escape and a backdrop click both cancel, which is the convention every other
 * panel in this game already uses.
 *
 * FOCUS goes to CANCEL, not to the confirm button. The player arrived here by
 * pressing or clicking something; landing focus on the destructive option
 * means a stray Enter completes the very action the prompt exists to slow
 * down. The banker's exchange overlay — this codebase's only other
 * confirm-before-commit — makes the same choice.
 */
export function confirmDialog(opts: {
  title: string;
  body: string;
  confirmLabel: string;
  onConfirm: () => void;
}): () => void {
  ensureChromeStyles();
  const overlay = document.createElement('div');
  overlay.className = 'tg-confirm';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.innerHTML = `
    <div class="tg-confirm-card">
      <p class="tg-confirm-title">${escapeHtml(opts.title)}</p>
      <p class="tg-confirm-body">${escapeHtml(opts.body)}</p>
      <div class="tg-confirm-actions">
        <button type="button" class="tg-confirm-btn" data-cancel>Cancel</button>
        <button type="button" class="tg-confirm-btn tg-confirm-go" data-confirm>${escapeHtml(opts.confirmLabel)}</button>
      </div>
    </div>`;

  const close = (): void => {
    document.removeEventListener('keydown', onKey, true);
    overlay.remove();
  };
  function onKey(e: KeyboardEvent): void {
    if (e.key !== 'Escape') return;
    e.preventDefault();
    // Capture phase: a panel underneath also listens for Escape, and without
    // this the one keypress would close both the prompt and whatever is behind
    // it.
    e.stopPropagation();
    close();
  }
  document.addEventListener('keydown', onKey, true);

  overlay.addEventListener('pointerdown', (e) => { if (e.target === overlay) close(); });
  overlay.querySelector('[data-cancel]')?.addEventListener('click', close);
  overlay.querySelector('[data-confirm]')?.addEventListener('click', () => {
    close();
    opts.onConfirm();
  });

  // Appended to BODY, not to the chrome bar: see the .tg-confirm rule above.
  document.body.appendChild(overlay);
  (overlay.querySelector('[data-cancel]') as HTMLButtonElement | null)?.focus();
  return close;
}

/**
 * The top-right toolbar: a Settings gear and Sign out.
 *
 * Janeks: *"Right-top - Sign Out styled red. Add exit icon. Make it grungy, Add
 * a gear button icon with 'settings' tooltip on hover."*
 *
 * Module-level and exported rather than a closure inside `mountGame`, so it can
 * be mounted on its own in a test. That is not tidiness: the thing worth
 * guarding is that the Sign out button goes through a CONFIRMATION, and while
 * the wiring lived inside a closure that first ran `loadGameState()` and loaded
 * a GLB, nothing could assert it — a test of `confirmDialog` alone passes
 * perfectly with the button wired straight to `onLogout`, which is exactly the
 * regression that matters.
 */
export function createChrome(opts: {
  /** Who is signed in — shown in the button's tooltip. */
  label: string;
  onLogout: () => void;
  /** Opens the settings screen. Optional: the gear is inert until one exists. */
  onSettings?: () => void;
}): { el: HTMLElement; teardown: () => void } {
  ensureChromeStyles();
  const bar = document.createElement('div');
  bar.className = 'tg-chrome';

  const settings = document.createElement('button');
  settings.type = 'button';
  settings.className = 'tg-chrome-btn tg-chrome-gear';
  settings.dataset.settings = '';
  // Not `disabled` when there is nothing behind it: a disabled button swallows
  // pointer events in Chrome, and the tooltip is the one thing this was asked
  // to do while the screen behind it did not exist.
  settings.title = 'Settings';
  settings.setAttribute('aria-label', 'Settings');
  settings.innerHTML = chromeIcon(GEAR_ICON);
  if (opts.onSettings) settings.addEventListener('click', opts.onSettings);

  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'tg-chrome-btn tg-chrome-signout';
  btn.dataset.signout = '';
  btn.title = `Sign out ${opts.label}`;
  btn.innerHTML = `${chromeIcon(EXIT_ICON)}<span>Sign out</span>`;

  // Asked for: a confirmation. Signing out drops the socket, clears every
  // module store and returns to /login, and the button sits two centimetres
  // from the gear — a misclick used to cost the whole session with no undo.
  let closeConfirm: (() => void) | null = null;
  const askSignOut = (): void => {
    closeConfirm?.();
    closeConfirm = confirmDialog({
      title: 'Sign out?',
      body: 'You will be returned to the login screen. Nothing in the settlement is lost.',
      confirmLabel: 'Sign out',
      onConfirm: () => { closeConfirm = null; opts.onLogout(); },
    });
  };
  btn.addEventListener('click', askSignOut);

  bar.append(settings, btn);
  return {
    el: bar,
    teardown: () => {
      btn.removeEventListener('click', askSignOut);
      // A Location visit tears this toolbar down. An orphaned prompt would
      // outlive its own button and still sign the player out when clicked.
      closeConfirm?.();
      closeConfirm = null;
      bar.remove();
    },
  };
}

/**
 * Mounts the chooser and resolves once the survivor exists.
 *
 * Returns `false` if the player never got that far — signing out mid-choice,
 * or the screen being torn down under them — so the caller can bail instead of
 * loading a body for a character that is not there.
 *
 * ─── Why the failure path keeps the screen up ───────────────────────────────
 * `createCharacter` leaves the store on `error` and rethrows. Dropping the
 * player into the game on a failed create would mean a settlement with no
 * survivor in it; showing them the error with their pick still selected costs
 * one line and is recoverable. The button re-arms itself, because the chooser
 * disabled it on press to stop a double-post.
 */
async function chooseCharacter(root: HTMLElement, user: AuthUser): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    let settled = false;
    const teardown = startCharacterSelect(root, {
      // The server takes no name — the account's is the survivor's. See the
      // dossier column in `character-select/index.ts`.
      playerName: user.username ?? user.email,
      // Rethrows on failure BY DESIGN: the chooser catches it, shows the
      // reason in its footer and re-arms its button (see its `onSelect`
      // docstring). Swallowing it here would leave the player watching
      // "Signing…" with the store quietly on `error`.
      onSelect: async ({ character }) => {
        await createCharacter(character.id);
        if (settled) return;
        settled = true;
        teardown();
        resolve(true);
      },
    });
    cancelChoice = () => {
      if (settled) return;
      settled = true;
      teardown();
      resolve(false);
    };
  });
}

/**
 * Tears down an open character chooser. Set while one is mounted so
 * `unmountGame` can resolve its promise instead of leaving it pending — the
 * screen holds a WebGL renderer and a rAF loop, the same leak
 * `disposeSettingsUI` exists to prevent.
 *
 * Module-level, alongside `currentTeardown`, and safe for the same reason:
 * `mountGame` is single-instance by construction — `main.ts` calls
 * `unmountGame()` before mounting again, and that path clears this. If a second
 * concurrent mount were ever introduced it would clobber the first chooser's
 * canceller and leak its context, which is exactly the hazard
 * `currentTeardown` already carries; the fix then is to make BOTH per-mount,
 * not to special-case this one.
 */
let cancelChoice: (() => void) | null = null;

/**
 * S6: server is the source of truth for the survivor, not localStorage. On
 * mount, fetches the Character/Health/Inventory/Equipment sheets — showing the
 * character chooser first if the account has no survivor yet (see
 * `chooseCharacter`) — loads the GLB for the character's own stored preset,
 * then goes into the settlement.
 */
export function mountGame(root: HTMLElement, user: AuthUser, onLogout: () => void): () => void {
  /**
   * The Settings screen (2026-09-16), wired to the gear `createChrome` has
   * always drawn and which was inert until now.
   *
   * Appended to `document.body`, NOT to `root`. Both views own `root` outright
   * and each does its own `root.innerHTML = ''` on mount, so a settings screen
   * living inside it would be destroyed by the settlement <-> Location swap —
   * and its module-level listeners would go on answering for an element that
   * no longer exists. It is a fixed full-screen overlay, so where it hangs
   * makes no visual difference.
   *
   * `confirmDialog` is passed IN rather than imported by that module: it lives
   * here, and this file imports that module, so the other direction would be a
   * cycle. See `settings-ui.ts`'s `confirmAction`.
   */
  const settingsEl = createSettingsUI({
    onSignOut: onLogout,
    confirm: confirmDialog,
  });
  document.body.appendChild(settingsEl);
  const openSettings = (): void => openSettingsUI();
  root.innerHTML = '';
  const prevHeight = root.style.height;
  const prevOverflow = root.style.overflow;
  root.style.height = '100dvh';
  root.style.overflow = 'hidden';

  let cancelled = false;
  let innerTeardown: (() => void) | null = null;

  /**
   * The top-right chrome: a Settings gear and the Sign out button.
   *
   * Janeks: *"Right-top - Sign Out styled red. Add exit icon. Make it grungy,
   * Add a gear button icon with "settings" tooltip on hover."*
   *
   * ─── Why the CSS is injected here and not in `ui/styles.ts` ───────────────
   * This chrome outlives the settlement view, so it needs a sheet that is not
   * torn down with that view.
   *
   * CORRECTION (2026-09-16): the reason this comment used to give was wrong.
   * It said the tokens live in the stylesheet `startBaseView` removes on
   * teardown, so `var(--rust)` would resolve to nothing mid-swap. Measured:
   * `ui/styles/` DECLARES no custom properties at all — it only uses them. The
   * palette is in `src/styles/app.css`, which `index.html` loads as a plain
   * global stylesheet and which never unmounts. Tokens would have been
   * available here all along.
   *
   * The hex stays anyway, on a different and honest reason: this toolbar is one
   * self-contained visual unit that deliberately does not match the in-game
   * panels, and half-tokenising it would be worse than either choice. The
   * hazard the old comment named is real — an undefined custom property kills
   * the whole declaration, not just the colour, which is how the vendor Buy
   * button once shipped transparent — it simply does not apply here.
   *
   * Injected once per page and deliberately not removed: `makeSignOut` runs
   * again on every settlement mount (a Location visit tears the toolbar down),
   * and a sheet with no elements to match costs nothing.
   */
  const makeSignOut = (): { el: HTMLElement; teardown: () => void } => {
    const chrome = createChrome({
      label: user.username ?? user.email,
      onLogout,
      onSettings: openSettings,
    });
    root.appendChild(chrome.el);
    return chrome;
  };

  const loadingEl = document.createElement('div');
  loadingEl.style.cssText =
    'display:flex;align-items:center;justify-content:center;height:100%;' +
    'font:14px ui-monospace,monospace;color:#b08a4e;background:#0a0705;';
  loadingEl.textContent = 'Loading...';
  root.appendChild(loadingEl);
  innerTeardown = () => loadingEl.remove();

  (async () => {
    try {
      // The landing page's bed and click sounds hand over here, and BEFORE the
      // two awaits below rather than after them: `startBaseView` plays the
      // SAME settlement loop at full gain on its own mixer, and a character
      // GLB can take a while to fetch — stopping afterwards would leave the
      // two beds overlapping for exactly that long. Signing out re-arms it.
      stopLandingAudio();

      await loadGameState();
      if (cancelled) return;

      /**
       * First login: the survivor does not exist yet, so ASK.
       *
       * `loadGameState` used to create one on the way past — a fixed preset,
       * posted silently — which made `character-select/`'s chooser
       * unreachable and handed every new player the same body. Reported
       * 2026-09-18 as "the character choosing step is completely gone from
       * the flow for new characters". The loader now stops on `uncreated`
       * (see its docstring) and the decision lands here, which is the file
       * that already owns what is mounted in `root`.
       *
       * Resolves once the row exists and the store is `ready`. The chooser
       * also hands back the GLB it was previewing, but it is deliberately
       * dropped: the preview loader and the in-world loader scale
       * differently, and taking the shortcut would put a preview-scaled
       * player in the settlement. The load below is cheap — the same URL is
       * in `loadCharacterModel`'s module-level cache by now.
       */
      let isNewSurvivor = false;
      if (gameStateStore.get().status === 'uncreated') {
        isNewSurvivor = true;
        loadingEl.remove();
        const chosen = await chooseCharacter(root, user);
        cancelChoice = null;
        if (cancelled || !chosen) return;
        // `root` is handed back empty by the chooser's teardown; the game's
        // own loading line goes back up while the body and sheets load.
        root.appendChild(loadingEl);
      }

      // Renders the player's OWN chosen preset. This was hardcoded to
      // `char-4-rigged.glb` while it was the only skinned character GLB — the
      // preset models shipped with `skins: 0`, so honouring the preset would
      // have made the player a statue (`CharacterAnimator` falls back to a
      // static pose rather than throwing, so it failed silently). All eight
      // presets now carry the same 20-bone skeleton, which
      // `character-models.test.ts` asserts from the shipped bytes — including
      // the bone names, since the clip library is shared and binds by name.
      // That test is what keeps this line safe: if a re-export ever drops a
      // skin, it fails there instead of silently producing a frozen player.
      //
      // Falls back to `char-4.glb` for an unrecognized `presetId` (a preset
      // removed from `CHARACTERS` while a character still references it) — a
      // known-rigged mesh beats rendering nothing. NOT `char-4-rigged.glb`:
      // that is the superseded 4749-tri body from before the 2026-09-09 art
      // replacement, kept only so nothing 404s, and it is excluded from both
      // the preset list and the editor's NPC roster.
      //
      // ACTIVE in practice by here: the loader only returns `ready` with a
      // created sheet, and the `uncreated` branch above does not fall through
      // until `createCharacter` has answered. The narrow stays because the
      // union permits UNCREATED, which carries no preset.
      const sheet = gameStateStore.get().character;
      const presetId = sheet?.state === 'ACTIVE' ? sheet.presetId : undefined;
      const presetUrl = CHARACTERS.find((c) => c.id === presetId)?.modelUrl;
      const model = await loadCharacterModel(presetUrl ?? '/models/char-4.glb');
      if (cancelled) return;

      connectGameStateSync();
      innerTeardown?.();
      root.innerHTML = '';
      // Phones and tablets play in landscape only (landscape-gate.ts).
      const stopLandscapeGate = mountLandscapeGate();

      // ── Settlement <-> Location (S17) ────────────────────────────────────
      //
      // Both views fully own `root` (each does its own `root.innerHTML = ''`
      // on mount), so swapping is tear-down-then-remount, not a live handoff.
      // `viewTeardown` always points at whichever one is currently mounted;
      // `signOut` is settlement-only chrome, torn down and re-added around it
      // rather than living outside the swap (a Location visit hides it, the
      // same way the settlement's other panels are absent there — rule 10).
      let viewTeardown: (() => void) | null = null;
      let signOutTeardown: (() => void) | null = null;
      /** Which match the arena view is currently mounted for, so a re-push of the same state does not remount it. */
      let mountedDuelId: string | null = null;
      /** Did the mounted duel actually start? Distinguishes an abandoned lobby from a finished fight — see the watcher. */
      let mountedWentLive = false;

      const mountSettlement = (): void => {
        mountedDuelId = null;
        mountedWentLive = false;
        viewTeardown?.();
        viewTeardown = startBaseView(root, model, handleEnterLocation);
        signOutTeardown = makeSignOut().teardown;
        resumeGoldPayment();
      };

      /**
       * Back from the payment gateway: core sends the player to
       * `/play?order=<id>`. The Buy Gold window opens and reports how the
       * payment went; the Gold itself arrives over `economy:updated`. Only on
       * the first settlement mount — the order param is stripped from the URL
       * as soon as it is read, so a later remount finds nothing anyway.
       */
      const resumeGoldPayment = (): void => {
        if (!new URLSearchParams(window.location.search).has('order')) return;
        openCurrencyShopUI();
        void resumeReturnedDeposit();
      };

      /**
       * Settlement -> arena, as soon as the server opens the LOBBY.
       *
       * ─── Why the swap is here and not inside the arena UI ─────────────────
       * Same reason the Location swap is: both views own `root` outright and
       * do their own `root.innerHTML = ''` on mount, so this is
       * tear-down-then-remount, and only the thing that holds `viewTeardown`
       * can do it safely. The arena modal lives INSIDE the settlement view and
       * would be destroying its own host.
       *
       * Until now nothing performed this swap at all: the modal simply hid
       * itself at `LIVE` and the duel went ahead somewhere the player could not
       * see.
       */
      const mountArena = (): void => {
        const match = arenaStateStore.get().view.match;
        if (!match) { mountSettlement(); return; }
        const entry = MAP_REGISTRY.find((m) => m.mapKey === match.mapKey);
        if (!entry) {
          // A map key the client does not carry is a content/deploy mismatch,
          // not a runtime condition. Staying in the settlement is wrong (the
          // duel is running regardless), so this is loud.
          console.error(`[bootstrap] arena map "${match.mapKey}" is not in MAP_REGISTRY — cannot mount the duel`);
          mountSettlement();
          const note = document.createElement('div');
          note.setAttribute('role', 'alert');
          note.textContent = 'The arena map could not be loaded. Returning to the settlement.';
          note.style.cssText = 'position:fixed;left:50%;transform:translateX(-50%);bottom:calc(1rem + env(safe-area-inset-bottom));z-index:9999;max-width:90vw;padding:0.75rem 1rem;background:#2a1010;color:#fff;border:1px solid #c44;border-radius:6px;font-size:0.9rem;text-align:center;';
          document.body.appendChild(note);
          window.setTimeout(() => note.remove(), 5000);
          return;
        }
        mountedDuelId = match.matchId;
        mountedWentLive = arenaStateStore.get().view.phase === 'LIVE';
        signOutTeardown?.();
        signOutTeardown = null;
        viewTeardown?.();
        viewTeardown = startArenaView(root, entry.data, match, () => {
          if (!cancelled) mountSettlement();
        }, model);
      };

      const mountLocation = (): void => {
        const instance = locationStateStore.get().active;
        if (!instance) { mountSettlement(); return; } // defensive — this is only invoked after a successful enter populated it
        signOutTeardown?.();
        signOutTeardown = null;
        viewTeardown?.();
        viewTeardown = startLocationView(root, instance, () => {
          if (!cancelled) mountSettlement();
        }, model);
      };

      function handleEnterLocation(): void {
        if (!cancelled) mountLocation();
      }

      /**
       * The practice yard: a client-only map a brand-new survivor lands in
       * BEFORE the settlement (movement, knife, gun, crate, gear, the map
       * guide). Like the arena and location swaps it owns `root`, so it is
       * mounted here and hands back to the settlement when it ends.
       */
      const mountYard = (): void => {
        mountedDuelId = null;
        mountedWentLive = false;
        signOutTeardown?.();
        signOutTeardown = null;
        viewTeardown?.();
        viewTeardown = startTutorialView(root, model, () => {
          if (!cancelled) mountSettlement();
        });
        signOutTeardown = makeSignOut().teardown;
      };

      // First login goes through the yard. It is keyed on the character having
      // JUST been created (server truth), so an existing player signing in on a
      // new device is never sent back to it; they can replay it from Settings.
      if (isNewSurvivor) mountYard(); else mountSettlement();

      const stopYardRequests = tutorialYardStore.subscribe((s) => {
        if (!s.requested || cancelled) return;
        clearTutorialYardRequest();
        mountYard();
      });

      // The duel's outcome arrives on the game socket, not the room (the room
      // is already disposing by then), so this subscription outlives the arena
      // view and is owned by the mount rather than by the view.
      const stopOutcome = connectBattlegroundOutcome();

      // ─── The arena socket outlives BOTH views, and must ───────────────────
      // `arena:state` used to be subscribed by the settlement view. That was
      // fine while the arena mounted at LIVE — the settlement was still up
      // for every push that mattered. It is not fine now that the swap happens
      // at LOBBY: tearing the settlement down unsubscribed the listener, so
      // the COUNTDOWN push carrying the seat reservation never arrived, and
      // the arena sat on a map it had loaded with no room to join. The duel
      // smoke probe caught it as "the client never joined the duel room".
      //
      // Owned here for the same reason `connectBattlegroundOutcome` is: it is
      // a subscription that spans a view swap, so it belongs to the thing
      // that performs the swap.
      const stopArena = connectArena();

      const stopArenaWatch = arenaStateStore.subscribe((s) => {
        if (cancelled) return;
        const match = s.view.match;
        // ─── The swap happens at LOBBY, not at LIVE ────────────────────────
        // Janeks: *"the countdown of 'no-one is moving' is when the pvp map is
        // already loaded. not on the matchmaking ui."* LOBBY is exactly the
        // phase that exists for loading the map — the server holds there until
        // both clients report `arena:loaded`, and only then mints seats and
        // starts the eight-second hold. Mounting at LIVE put the load AFTER
        // the countdown it was supposed to happen during, so the freeze ran
        // over the lobby modal and the arena appeared when it ended.
        //
        // COUNTDOWN and LIVE are still listed: a reconnect, or a client that
        // was elsewhere when LOBBY went by, must still end up in the arena.
        const inArena = s.view.phase === 'LOBBY' || s.view.phase === 'COUNTDOWN' || s.view.phase === 'LIVE';
        if (inArena && match && mountedDuelId !== match.matchId) { mountArena(); return; }
        if (s.view.phase === 'LIVE' && match && mountedDuelId === match.matchId) mountedWentLive = true;
        // ─── And back out again if the lobby never becomes a duel ──────────
        // Nothing used to mount before LIVE, so an abandoned lobby — the
        // opponent dropping during the load, or the join window lapsing into
        // a cancel — had no route home. Now it would strand the player in an
        // arena with no room to join and no opponent coming.
        //
        // Only for a duel that never went LIVE. A FINISHED duel also returns
        // the arena phase to IDLE, and remounting there would snatch the
        // result card away before the player had read it — the one piece of
        // feedback the whole duel produces. That exit belongs to the card's
        // own button (`startArenaView`'s `onExit`).
        if (!inArena && mountedDuelId !== null && !mountedWentLive) mountSettlement();
      });

      innerTeardown = () => {
        stopLandscapeGate();
        stopYardRequests();
        stopArenaWatch();
        stopArena();
        stopOutcome();
        resetBattleground();
        disconnectGameStateSync();
        // Every module-level store the game populates gets cleared here, not
        // just game-state. These are module memory that outlives the view, so
        // whatever is left behind is what the NEXT account sees on mount —
        // account B would open onto A's quest board and A's `!`/`?` markers
        // until the refetch landed. `resetNpcState`'s own docstring has said
        // "called on sign-out so a second account never sees the first one's
        // session" since S7; it was never actually called until now.
        resetGameState();
        resetNpcState();
        resetQuestState();
        signOutTeardown?.();
        viewTeardown?.();
      };
    } catch (err) {
      if (cancelled) return;
      loadingEl.textContent = 'Failed to load your survivor. Please refresh.';
      console.error('[bootstrap] game state load failed', err);
    }
  })();

  currentTeardown = () => {
    cancelled = true;
    // Before `innerTeardown`, and unconditionally: an open chooser holds a
    // WebGL renderer and a rAF loop, and `chooseCharacter`'s promise is
    // otherwise never settled — signing out mid-choice would leave both
    // running for the next account. `cancelled` alone does not reach them:
    // the mount is parked on that `await`.
    cancelChoice?.();
    cancelChoice = null;
    innerTeardown?.();
    // Out here rather than in `innerTeardown`, which is only assembled once
    // the survivor has loaded — a mount that is cancelled or that fails on
    // `loadGameState` would otherwise leave this screen's WebGL preview and
    // its document-level key listener behind for the next account.
    disposeSettingsUI();
    root.style.height = prevHeight;
    root.style.overflow = prevOverflow;
  };
  return currentTeardown;
}

export function unmountGame(): void {
  currentTeardown?.();
  currentTeardown = null;
}
